"use client";

import {
  createContext,
  createElement,
  forwardRef,
  useContext,
  type ImgHTMLAttributes,
  type ReactNode,
} from "react";
import {
  resolveMediaAlt,
  type MediaDescriptions,
} from "@/utils/media-descriptions";

const Descriptions = createContext<MediaDescriptions>({});

export function MediaDescriptionProvider({
  descriptions,
  children,
}: {
  descriptions: MediaDescriptions;
  children: ReactNode;
}) {
  return (
    <Descriptions.Provider value={descriptions}>
      {children}
    </Descriptions.Provider>
  );
}

export function useMediaDescriptions(): MediaDescriptions {
  return useContext(Descriptions);
}

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
  legacyAlt?: string;
  /** Used when there is neither a legacy alt nor a description; unlike `alt=""`, an empty fallback still lets the description apply. */
  fallbackAlt?: string;
}

const MediaImage = forwardRef<HTMLImageElement, Props>(function MediaImage(
  { legacyAlt, fallbackAlt, ...props },
  ref,
) {
  const descriptions = useContext(Descriptions);
  return createElement("img", {
    ...props,
    alt: resolveMediaAlt(
      descriptions,
      props.src ?? "",
      fallbackAlt ?? props.alt ?? "billede",
      legacyAlt,
      (props.alt === "" && fallbackAlt === undefined) ||
        props.role === "presentation" ||
        props["aria-hidden"] === true ||
        props["aria-hidden"] === "true",
    ),
    ref,
  });
});

export default MediaImage;
